/**
 * Carte d'affichage d'un auteur
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'affichage d'un auteur
 *
 * @module AuteurCard
 */

import Image from 'next/image';
import { useMemo } from 'react';

import { computeAuteurPhoto } from '@/components/ui/auteurs/utils/computeAuteurPhoto';
import { IMAGES_ASSETS_PREFIX } from '@/constants';
import { IMAGE_QUALITY_ENTITY_CARD } from '@/constants/image-quality';
import { Link } from '@/i18n/navigation';
import type { Meilisearch_Auteur } from '@/lib/api/meilisearch-types';

// ============================================================================
// Types
// ============================================================================

export interface AuteurCardProps {
  /**
   * Données de l'auteur
   */
  auteur: Meilisearch_Auteur;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Carte d'affichage d'un auteur
 *
 * @example
 * ```tsx
 * <AuteurCard auteur={auteurData} />
 * ```
 */
export function AuteurCard({ auteur }: AuteurCardProps) {
  const photo = useMemo(() => computeAuteurPhoto(auteur), [auteur]);

  return (
    <Link
      href={{
        pathname: `/auteurs/[slug]`,
        params: { slug: auteur.slug! },
      }}
      className="group card card-compact relative size-full overflow-hidden rounded-md bg-base-100 shadow-sm outline-none transition-all hover:bg-secondary-50 focus:outline-none focus-visible:outline-none active:scale-[.97]"
    >
      <figure className="relative aspect-[12/15] transition-all sm:group-hover:scale-105">
        {photo ? (
          <Image
            src={`${IMAGES_ASSETS_PREFIX}/${photo}?fit=cover&width=255&height=317&quality=${IMAGE_QUALITY_ENTITY_CARD}`}
            alt={auteur.identite_complete}
            fill
            quality={IMAGE_QUALITY_ENTITY_CARD}
            sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 33vw, 20vw"
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200" />
        )}
      </figure>
      <div className="inset-x-0 bottom-0 z-[1] flex h-16 place-content-center items-center bg-white px-3 text-center font-gothic text-gray-700">
        <h5 className="text-center font-gothicb text-sm leading-tight">
          {auteur.identite_complete}
        </h5>
      </div>
    </Link>
  );
}
